<template lang="pug">
main.newsfeed.container.pt-6.md_pt-12.lg_pt-18
  header.flex.justify-between.flex-wrap.gap-y-6.w-full.mb-6.md_mb-0.md_flex-wrap-reverse
    h2.whitespace-nowrap.mb-8 Press Releases
    LayoutSearchBar.self-end(
      searchPlaceholder=""
      :previousSearchQuery="query"
      @on-search="query = $event"
      @clear-previous-search-query="query = ''"
      analyticsEventNamePrefix="newsfeed"
    )

  .font-medium.text-gray-600.text-sm.mb-6(v-if="query && results.length > 0")
    ClientOnly
      span(v-if="maxPage > 0") Showing {{ page * LIMIT + 1 }} &ndash; {{ page * LIMIT + results.length }} of {{ count }} results for &ldquo;{{ query }}&rdquo;
      span(v-else) Showing {{ count }} results for &ldquo;{{ query }}&rdquo;
      a.ml-4.cursor-pointer.text-copper-800.underline.hover_underline.hover_text-copper-900(@click="query = ''") Clear

  .newsfeed-listing
    nav.newsfeed-tabs
      ul.flex.w-full.justify-center.cursor-pointer.border-b.border-gray-300.text-sm.text-gray-700.mb-0.font-medium
        li
          a(@click="type = 'vrified'" :class="{ active: type === 'vrified' }" v-analytics="'newsfeedVrifiedTabSelected'") VRIFIED
        li(v-if="isLoggedIn")
          a(@click="type = 'following'" :class="{ active: type === 'following' }" v-analytics="'newsfeedFollowingTabSelected'") Following
        li
          a(@click="type = 'all'" :class="{ active: type === 'all' }" v-analytics="'newsfeedAllTabSelected'") All
      .underline.transition-transform.transform(
        :class="{ 'w-1/3': isLoggedIn, 'w-1/2': !isLoggedIn, 'translate-x-0': type === 'vrified', 'translate-x-full': type === 'following' || (type === 'all' && !isLoggedIn), 'translate-x-double': type === 'all' && isLoggedIn }"
      )

    LoadingSpinner.my-16(v-if="loading")
    div(v-else-if="!!results.length")
      article.my-16(v-for="post in results")
        .flex.justify-between.items-end
          .h3.uppercase.font-semibold.text-lg.text-gray-600.mb-0 {{ post.company.name }}
          button.flex.p-0.bg-transparent.text-sm.hover_underline(@click="shareItem = post")
            ArrowUpTrayIcon.mr-2.w-5.h-5
            | Share
        NuxtLink.newsfeed-post.block.text-black(
          @click.native="trackNewsItemClicked(post)"
          :to="{ name: 'press-releases-id', params: { id: post.url_slug }, query: { referrer: 'press-releases' } }"
        )
          .deck-slide.mt-1.ml-4.mb-4.float-right.relative(v-if="post.deck" :style="{ width: '16rem', height: '9rem' }")
            img.absolute.inset-0.object-cover.w-full.h-full.rounded-lg.border.border-solid.border-gray-300(
              v-if="post.deck.deck_image"
              :src="post.deck.deck_image"
              :alt="post.deck.title"
            )
            .flex.justify-start.absolute.bottom-2.left-2.gap-2.inset-x-0
              ThreeDPill(v-if="post.deck.tags.indexOf('3D') > -1")
              ThreeSixtyPill(v-if="post.deck.tags.indexOf('360') > -1")
          .h2.text-lg.text-gray-900.font-medium.my-4 {{ decode(post.title) }}
          p.mb-3.leading-6(v-if="post.content") {{ post.content ? formatPostContent(post.content) : '' }}
          .h5.text-gray-600.text-sm.font-medium {{ formatPostDate(post.date) }} {{ post.author ? ' | ' + post.author : '' }}
    .my-16(v-else)
      .text-center There are no posts with this criteria to display.

    VPaginateListed(
      aria-label="Newsfeed Pagination"
      v-if="!!results.length"
      :currentPage="page"
      :max="maxPage"
      @change-page="page = $event"
    )

  ModalsShare(
    :share-url="getUrl(shareItem) || ''"
    :title="shareItem ? decode(shareItem.title) : ''"
    :showing="shareItem"
    analytics-prefix="PressReleasesPage"
    @update:showing="shareItem = false"
  )
</template>

<script lang="ts" setup>
import * as R from 'ramda'
import { ArrowUpTrayIcon } from '@heroicons/vue/24/solid'
import type { NewsfeedTypeParam } from '@/composables/useNewsfeedSearch'
import type { NewsfeedPost } from '@/entities/newsfeedPost'
import type { Company } from '@/entities/company'
import { decode } from 'html-entities'
import ThreeDPill from '@/components/companies/icons/ThreeDPill.svg'
import ThreeSixtyPill from '@/components/companies/icons/ThreeSixtyPill.svg'
import { formatDateFull } from '@/lib/datetime-format-utils'

const NuxtLink = resolveComponent('NuxtLink')

const { state } = useUser()
const { $analytics } = useNuxtApp()
const shareItem = ref<NewsfeedPost | null>(null)
const isLoggedIn = computed(() => !!state.value.user)

const LIMIT = 10
const { query, type, limit, page, maxPage, results, count, loading } = useNewsfeedSearch({
  query: process.client ? sessionStorage?.getItem('postsQuery') || '' : undefined,
  type: process.client ? <NewsfeedTypeParam | null>sessionStorage?.getItem('postsType') || 'vrified' : undefined,
  page: process.client ? parseInt(sessionStorage?.getItem('postsPageNum') || '0', 10) : undefined,
  limit: LIMIT,
  clientVal: process.client,
})

watch(query, val => (process.client ? sessionStorage?.setItem('postsQuery', val || '') : undefined))
watch(type, val => (process.client ? sessionStorage?.setItem('postsType', val) : undefined))
watch(page, val => (process.client ? sessionStorage?.setItem('postsPageNum', `${val}`) : undefined))

onMounted(() => {
  if (!process.client) return

  const savedTab = <NewsfeedTypeParam | null>sessionStorage?.getItem('postsType')
  if (savedTab) type.value = savedTab
  const savedPage = sessionStorage?.getItem('postsPageNum')
  if (savedPage) page.value = parseInt(savedPage, 10)
  const savedQuery = sessionStorage?.getItem('postsQuery')
  if (savedQuery) query.value = savedQuery
})

watch(page, () => {
  // asynchronous execution because firefox wouldn't do it the straightforward way
  setTimeout(() => {
    window.scrollTo({ behavior: 'smooth', top: 0 })
  }, 0)
})

/* analytics */
onMounted(() => {
  $analytics.track('showedScreenNewsfeed')
})

watch(page, (n: number) => {
  $analytics.track('newsfeedLoadedMorePosts', {
    itemsLoaded: limit.value,
    pageNumber: n,
  })
})

watch(
  () => query.value,
  searchQuery => {
    if (searchQuery) {
      $analytics.track('postsSearchSubmitted', { searchQuery })
    }
  }
)

function trackNewsItemClicked({ title, id, company }: { title: string; id: number; company: Company }) {
  const isFollowing = R.includes(
    company.id,
    state.value.followingCompanies.map(({ id }) => id)
  )
  const listPosition: number = results.value.findIndex(result => result.id === id)

  $analytics.track('newsfeedNewsItemSelected', {
    newsTitle: title,
    newsId: id,
    listPosition: listPosition,
    isFollowing,
  })
}

const formatPostDate = formatDateFull

function formatPostContent(content: string) {
  content = content.replace(/(<([^>]+)>)/gi, '')
  content = decode(content)
  content = content.slice(0, 550)
  content = content + '\u2026' // horizontal ellipsis
  return content
}

function getUrl(item: NewsfeedPost) {
  return item && window.location.origin + window.location.pathname + '/' + item.url_slug
}
</script>

<style lang="stylus" scoped>
a:hover
  color unset // undo some default styles
header h2
  @apply text-4xl font-bold
.newsfeed-listing
  .newsfeed-tabs
    ul li
      @apply flex-1 text-center
      a
        color inherit
        @apply block pb-4
      a.active
        @apply text-copper-700
      &:hover
        @apply text-gray-900
    .underline
      @apply border-b-2 border-copper-700 relative -mt-px

  article
    a:hover .h2
      text-decoration: underline
</style>
